import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  RefreshCw,
  AlertTriangle
} from 'lucide-react'
import { getStoredAccounts, generateOAuthUrl } from '@/features/gmb/api'

interface GMBAuthStatusProps {
  onAuthSuccess?: () => void
}

interface AuthStatus {
  isAuthenticated: boolean
  accounts: Array<{ name?: string; account_name?: string; id?: string; account_id?: string }>
  error?: string
  lastChecked?: Date
}

export default function GMBAuthStatus({ onAuthSuccess }: GMBAuthStatusProps) {
  const [status, setStatus] = useState<AuthStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const checkAuthStatus = useCallback(async () => {
    try {
      setRefreshing(true)

      // Check if we have stored accounts (indicates authentication)
      const result = await getStoredAccounts()

      if (result.success) {
        setStatus({
          isAuthenticated: result.data?.accounts && result.data.accounts.length > 0,
          accounts: result.data?.accounts || [],
          lastChecked: new Date()
        })

        if (result.data?.accounts && result.data.accounts.length > 0 && onAuthSuccess) {
          onAuthSuccess()
        }
      } else {
        setStatus({
          isAuthenticated: false,
          accounts: [],
          error: result.error || 'Failed to check authentication status',
          lastChecked: new Date()
        })
      }
    } catch (error) {
      setStatus({
        isAuthenticated: false,
        accounts: [],
        error: error instanceof Error ? error.message : 'Network error checking authentication',
        lastChecked: new Date()
      })
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [onAuthSuccess])

  const initiateOAuth = async () => {
    try {
      const result = await generateOAuthUrl()

      if (result.success && result.data?.auth_url) {
        // Open OAuth URL in new tab
        window.open(result.data.auth_url, '_blank', 'width=600,height=700')

        // Set up polling to check auth status
        const pollInterval = setInterval(() => {
          void checkAuthStatus().then(() => {
            if (status?.isAuthenticated) {
              clearInterval(pollInterval)
            }
          })
        }, 3000)

        // Clear polling after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000)
      } else {
        throw new Error(result.error || 'Failed to get OAuth URL')
      }
    } catch (error) {
      console.error('OAuth initiation failed:', error)
      setStatus(prev => prev ? {
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to initiate OAuth'
      } : null)
    }
  }

  useEffect(() => {
    void checkAuthStatus()
  }, [checkAuthStatus])

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Checking GMB authentication status...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!status) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Unable to check GMB authentication status. Please try refreshing the page.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Google My Business Authentication
              {status.isAuthenticated ? (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <XCircle className="h-3 w-3 mr-1" />
                  Not Connected
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {status.isAuthenticated
                ? `Connected with ${status.accounts.length} Google account(s)`
                : 'Authentication required to access Google My Business features'
              }
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void checkAuthStatus() }}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!status.isAuthenticated && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You need to authenticate with Google My Business to access GMB features like reviews, posts, and location management.
            </AlertDescription>
          </Alert>
        )}

        {status.error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Error:</strong> {status.error}
            </AlertDescription>
          </Alert>
        )}

        {status.isAuthenticated && status.accounts.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Connected Google Accounts:</h4>
            <div className="space-y-2">
              {status.accounts.map((account, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <div className="font-medium">{account.name || account.account_name || 'Unknown Account'}</div>
                    <div className="text-sm text-muted-foreground">{account.id || account.account_id}</div>
                  </div>
                  <Badge variant="outline">Active</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {!status.isAuthenticated && (
            <Button onClick={initiateOAuth} className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Connect Google My Business
            </Button>
          )}

          <Button variant="outline" onClick={() => { void checkAuthStatus() }} disabled={refreshing}>
            {refreshing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              'Refresh Status'
            )}
          </Button>
        </div>

        {status.lastChecked && (
          <p className="text-xs text-muted-foreground">
            Last checked: {status.lastChecked.toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
